// PO1-2746: página pública mínima de upload do kit de onboarding.
// Acessada via link único com JWT no path: /public/onboarding-kit/:token
//
// Diferente do CotacaoSpot (PO1-2057, que usa ApiHubBackend porque as rotas
// dele vivem no hub-backend legado): as rotas /public/onboarding-kit/* vivem
// no backend_hub, mesmo backend do wizard — usa o client `services/api`
// normal (sem sessão, os interceptors de auth simplesmente não fazem nada).
//
// Nunca exibe nome, razão social ou CNPJ do embarcador — nem o preview nem
// a resposta do upload trazem esse dado (garantido pelo backend).

import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, CardBody, Input, Spinner } from "reactstrap";
import api from "../../../services/api";
import autopilotLogo from "../../../assets/img/autopilot-logo.png";

const SHELL_STYLE = {
  minHeight: "100vh",
  background: "var(--everlog-card-hero-bg)",
  padding: "32px 16px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
};

const BRAND_HEADER_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginBottom: 24,
};

const BRAND_LOGO_STYLE = { width: 44, height: 44, objectFit: "contain" };

const BRAND_TEXT_STYLE = {
  color: "var(--everlog-card-title)",
  fontSize: 22,
  fontWeight: 600,
  letterSpacing: 0.3,
};

const FOOTER_STYLE = {
  marginTop: 32,
  textAlign: "center",
  color: "var(--everlog-card-text-soft)",
  fontSize: 12,
  letterSpacing: 0.3,
};

const CARD_STYLE = {
  borderRadius: 12,
  boxShadow: "var(--everlog-card-elevated-shadow)",
  border: "1px solid var(--everlog-card-border-soft)",
  background: "var(--everlog-card-surface-elevated)",
  overflow: "hidden",
};

const HEADER_STYLE = {
  background:
    "linear-gradient(135deg, var(--theme-accent-surface, var(--theme-accent)), var(--theme-accent-surface-strong, var(--theme-accent-strong)))",
  color: "var(--everlog-on-accent-text)",
  padding: "20px 28px",
};

const FIELD_LABEL_STYLE = {
  fontWeight: 600,
  color: "var(--everlog-card-title)",
  marginBottom: 4,
  fontSize: 14,
};

const FIELD_HELP_STYLE = {
  fontSize: 12,
  color: "var(--everlog-card-muted)",
  marginBottom: 8,
};

const ERROR_MAP = {
  token_invalido: "Link inválido. Verifique se você copiou a URL completa do e-mail.",
  link_inutilizavel:
    "Esse link expirou ou já não está mais disponível. Peça um novo ao seu contato na Everlog.",
  limite_arquivos_excedido:
    "Muitos arquivos de uma vez. Tente compactar os arquivos num .zip e enviar esse arquivo único.",
};

const Shell = ({ children }) => (
  <div style={SHELL_STYLE}>
    <div style={{ width: "100%", maxWidth: 640 }}>
      <div style={BRAND_HEADER_STYLE}>
        <img src={autopilotLogo} alt="" style={BRAND_LOGO_STYLE} aria-hidden="true" />
        <span style={BRAND_TEXT_STYLE}>Everlog</span>
      </div>
      {children}
      <div style={FOOTER_STYLE}>
        Este link foi enviado pela equipe de implantação da Everlog. Em caso
        de dúvida, entre em contato com quem enviou o e-mail.
      </div>
    </div>
  </div>
);

const CAMPOS = [
  {
    key: "planilha",
    label: "Planilha de onboarding preenchida",
    help: "O arquivo .xlsx que você recebeu por e-mail, já preenchido.",
    accept: ".xlsx,.xls",
    multiple: false,
  },
  {
    key: "baseMovimento",
    label: "Base de movimento (opcional agora)",
    help: "Export de um mês fechado do seu sistema. Pode enviar depois, pelo mesmo link.",
    accept: ".csv,.xls,.xlsx,.txt,.zip,.pdf,.xml",
    multiple: true,
  },
  {
    key: "tabelas",
    label: "Tabelas de preço das transportadoras",
    help: "Um ou mais arquivos (xls, xlsx ou pdf).",
    accept: ".xls,.xlsx,.pdf,.zip",
    multiple: true,
  },
];

export default function OnboardingKit() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState(null);
  const [preview, setPreview] = useState(null);
  const [arquivos, setArquivos] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorCode(null);
    api
      .get(`/public/onboarding-kit/${token}`)
      .then((response) => {
        if (cancelled) return;
        setPreview(response.data);
      })
      .catch((err) => {
        if (cancelled) return;
        const code = (err.response && err.response.data && err.response.data.error) || "internal_error";
        setErrorCode(code);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleFileChange = useCallback((campo, multiple) => (event) => {
    const files = multiple ? Array.from(event.target.files || []) : event.target.files[0];
    setArquivos((prev) => ({ ...prev, [campo]: files }));
  }, []);

  const podeEnviar = Object.values(arquivos).some((v) =>
    Array.isArray(v) ? v.length > 0 : !!v
  );

  const handleSubmit = useCallback(async () => {
    if (!podeEnviar) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const formData = new FormData();
      CAMPOS.forEach(({ key, multiple }) => {
        const v = arquivos[key];
        if (!v) return;
        if (multiple) {
          v.forEach((f) => formData.append(key, f));
        } else {
          formData.append(key, v);
        }
      });
      const response = await api.post(`/public/onboarding-kit/${token}/upload`, formData);
      setResultado(response.data);
    } catch (err) {
      const code = (err.response && err.response.data && err.response.data.error) || null;
      setSubmitError(
        (code && ERROR_MAP[code]) ||
          "Não foi possível enviar os arquivos agora. Tente novamente em instantes."
      );
    } finally {
      setSubmitting(false);
    }
  }, [arquivos, podeEnviar, token]);

  if (loading) {
    return (
      <Shell>
        <Card style={CARD_STYLE}>
          <CardBody style={{ textAlign: "center", padding: 48 }}>
            <Spinner color="primary" />
            <p style={{ marginTop: 16, color: "var(--everlog-card-muted)" }}>
              Carregando…
            </p>
          </CardBody>
        </Card>
      </Shell>
    );
  }

  if (errorCode && !preview) {
    return (
      <Shell>
        <Card style={CARD_STYLE}>
          <CardBody style={{ padding: 32 }}>
            <h4 style={{ color: "var(--everlog-status-danger)", marginBottom: 12 }}>
              Não foi possível abrir este link
            </h4>
            <p style={{ color: "var(--everlog-card-text)" }}>
              {ERROR_MAP[errorCode] ||
                "Algo deu errado ao abrir este link. Tente novamente em instantes."}
            </p>
          </CardBody>
        </Card>
      </Shell>
    );
  }

  if (resultado) {
    const recebido = resultado.recebido || {};
    const itensRecebidos = CAMPOS.filter((c) => recebido[c.key]);
    return (
      <Shell>
        <Card style={CARD_STYLE}>
          <div style={HEADER_STYLE}>
            <h3 style={{ margin: 0, fontWeight: 600 }}>Arquivos recebidos</h3>
          </div>
          <CardBody style={{ padding: 32 }}>
            {itensRecebidos.length > 0 ? (
              <ul style={{ color: "var(--everlog-card-text)" }}>
                {itensRecebidos.map((c) => (
                  <li key={c.key}>{c.label}</li>
                ))}
              </ul>
            ) : (
              <p style={{ color: "var(--everlog-card-text)" }}>
                Recebemos seu envio.
              </p>
            )}
            <p style={{ color: "var(--everlog-card-text-soft)", fontSize: 14, marginTop: 16 }}>
              Obrigado! A equipe Everlog foi notificada. Se ainda faltar algum
              arquivo, pode enviar depois pelo mesmo link.
            </p>
          </CardBody>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <Card style={CARD_STYLE}>
        <div style={HEADER_STYLE}>
          <h3 style={{ margin: 0, fontWeight: 600 }}>Kit de implantação</h3>
        </div>
        <CardBody style={{ padding: "28px 32px" }}>
          <p style={{ color: "var(--everlog-card-text)", marginBottom: 20 }}>
            Envie os arquivos abaixo. Você pode enviar tudo de uma vez ou aos
            poucos — cada envio atualiza o que já recebemos.
          </p>

          {CAMPOS.map((campo) => (
            <div key={campo.key} style={{ marginBottom: 18 }}>
              <div style={FIELD_LABEL_STYLE}>{campo.label}</div>
              <div style={FIELD_HELP_STYLE}>{campo.help}</div>
              <Input
                type="file"
                accept={campo.accept}
                multiple={campo.multiple}
                onChange={handleFileChange(campo.key, campo.multiple)}
              />
            </div>
          ))}

          {submitError && (
            <div style={{ color: "var(--everlog-status-danger)", marginBottom: 16, fontSize: 14 }}>
              {submitError}
            </div>
          )}

          <Button
            color="primary"
            block
            disabled={!podeEnviar || submitting}
            onClick={handleSubmit}
            style={{ marginTop: 8, padding: 12, fontWeight: 600 }}
          >
            {submitting ? <Spinner size="sm" /> : "Enviar arquivos"}
          </Button>
        </CardBody>
      </Card>
    </Shell>
  );
}
